import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../api/client'
import type { IntakeCommitResult, IntakeMeta, LookupItem, PreviewResult } from '../api/types'
import { useAsync } from '../hooks/useAsync'
import { useOperation } from '../hooks/useOperation'
import { Badge, Field, Notice, fmtTime } from '../components/ui'

const CLASS_LABEL: Record<string, string> = {
  NEW: '可入库',
  DUP_IN_BATCH: '本批重复',
  DUP_ACTIVE: '已在库/已售',
  INVALID: '格式非法',
}
const CLASS_TONE: Record<string, string> = { NEW: 'ok', DUP_IN_BATCH: 'warn', DUP_ACTIVE: 'warn', INVALID: 'muted' }

const emptyMeta = (): IntakeMeta => ({
  zone: 'ZONE_A',
  platform: '',
  registerCode: '',
  remark: '',
})

export function Intake() {
  const [meta, setMeta] = useState<IntakeMeta>(emptyMeta)
  const [text, setText] = useState('')
  const [preview, setPreview] = useState<PreviewResult | null>(null)
  const [previewBusy, setPreviewBusy] = useState(false)
  const [previewError, setPreviewError] = useState<unknown>(null)
  const op = useOperation<IntakeCommitResult>('intake')

  const plat = useAsync(() => api.get<{ items: LookupItem[] }>('/api/platforms'), [])
  const platforms = plat.data?.items ?? []

  // 平台来自名单：名单被清空或删掉当前选项后，选择必须一起作废，否则提交会被服务器拒绝。
  useEffect(() => {
    if (meta.platform && platforms.length > 0 && !platforms.some((p) => p.name === meta.platform)) {
      setMeta({ ...meta, platform: '' })
      setPreview(null)
    }
  }, [meta, platforms])

  const lines = useMemo(
    () =>
      text
        .split(/[\n,;，、]+/)
        .map((s) => s.trim())
        .filter(Boolean),
    [text],
  )

  const expired = preview ? new Date(preview.expiresAt).getTime() < Date.now() : false
  const commitable = !!preview && preview.counts.new > 0 && !expired && !op.busy

  async function doPreview() {
    setPreviewBusy(true)
    setPreviewError(null)
    try {
      setPreview(await api.post<PreviewResult>('/api/intake/preview', { meta, domains: lines }))
      op.newIntent()
    } catch (e) {
      setPreviewError(e)
      setPreview(null)
    } finally {
      setPreviewBusy(false)
    }
  }

  function resetPreview() {
    setPreview(null)
    op.newIntent()
  }

  async function doCommit() {
    if (!preview) return
    const token = preview.token
    const domains = lines
    const res = await op.run((key) =>
      api.post<IntakeCommitResult>('/api/intake/commit', { token, meta, domains }, key),
    )
    if (res) {
      setPreview(null)
      setText('')
    }
  }

  return (
    <section>
      <header className="page-head compact">
        <h1>批量入库</h1>
        <span className="muted">单次最多 300 行</span>
      </header>

      <div className="grid two">
        <div className="card">
          <h2>1. 本批公共信息</h2>
          <div className="grid two">
            <Field label="区域">
              <select
                value={meta.zone}
                onChange={(e) => {
                  setMeta({ ...meta, zone: e.target.value as IntakeMeta['zone'] })
                  resetPreview()
                }}
              >
                <option value="ZONE_A">A 区</option>
                <option value="ZONE_S">S 区</option>
              </select>
            </Field>
            <Field label="平台">
              <select
                value={meta.platform}
                onChange={(e) => {
                  setMeta({ ...meta, platform: e.target.value })
                  resetPreview()
                }}
                disabled={platforms.length === 0}
              >
                <option value="">{platforms.length === 0 ? '名单为空' : '请选择…'}</option>
                {platforms.map((p) => (
                  <option key={p.id} value={p.name}>
                    {p.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="注册码" hint="最多 64 字">
              <input
                maxLength={64}
                value={meta.registerCode}
                onChange={(e) => {
                  setMeta({ ...meta, registerCode: e.target.value })
                  resetPreview()
                }}
              />
            </Field>
          </div>
          <Field label="备注" hint="最多 300 字">
            <input
              maxLength={300}
              value={meta.remark}
              onChange={(e) => {
                setMeta({ ...meta, remark: e.target.value })
                resetPreview()
              }}
            />
          </Field>
          {platforms.length === 0 && !plat.loading && (
            <div className="notice warn">
              平台名单还是空的 —— <Link to="/settings">到设置页添加平台</Link>，之后这里才有下拉选项。
            </div>
          )}
          <Notice error={plat.error} />
          <p className="muted">
            注册日期不用填：提交那一刻系统按营业时区自动生成收录日期。预览里会先告诉你这批会写成哪一天。
          </p>

          <h2>2. 域名清单</h2>
          <textarea
            rows={12}
            aria-label="域名清单，每行一个"
            value={text}
            onChange={(e) => {
              setText(e.target.value)
              setPreview(null)
            }}
            placeholder={'每行一个域名，例如\n003929.com\nshop.co.uk'}
          />
          <div className="row spread">
            <span className="muted" role="status">
              已识别 {lines.length} 行
            </span>
            <div className="row">
              <button
                type="button"
                onClick={() => {
                  setText('')
                  resetPreview()
                }}
                disabled={!lines.length}
              >
                清空
              </button>
              <button
                type="button"
                className="primary"
                onClick={doPreview}
                disabled={!lines.length || previewBusy || !meta.platform}
              >
                {previewBusy ? '预览中…' : '预览 / 去重'}
              </button>
            </div>
          </div>
          {/* 按钮禁用时必须说明为什么：只靠灰掉，操作员会以为页面卡住。 */}
          {!lines.length ? (
            <p className="sub">先在上方粘贴域名，「预览 / 去重」才能点。</p>
          ) : !meta.platform ? (
            <p className="sub">先在上方选择平台，「预览 / 去重」才能点。</p>
          ) : null}
          <Notice error={previewError} />
        </div>

        <div className="card">
          <h2>3. 预览与提交</h2>
          {!preview && <p className="muted">选平台、粘贴清单后点「预览 / 去重」。改动清单或公共信息后需重新预览。</p>}
          {preview && (
            <>
              <div className="row wrap counts">
                <Badge tone="ok">可入库 {preview.counts.new}</Badge>
                <Badge tone="muted">已在库/已售 {preview.counts.dupActive} · 自动跳过</Badge>
                <Badge tone="muted">本批重复 {preview.counts.dupBatch} · 自动跳过</Badge>
                <Badge tone="muted">格式非法 {preview.counts.invalid} · 不写入</Badge>
                <Badge>合计 {preview.counts.total}</Badge>
              </div>
              <p className="muted">
                收录日期 <strong>{preview.registeredOn}</strong>（自动生成）· {preview.meta.platform} ·{' '}
                {preview.meta.zone === 'ZONE_A' ? 'A 区' : 'S 区'}
              </p>
              <p className="muted">有效期至 {fmtTime(preview.expiresAt)} · 库存版本 {preview.inventoryVersion}</p>
              {expired && <div className="notice warn">预览已过期或库存已变动，请重新预览后再提交。</div>}
              <div className="scroll-x">
                <table className="table compact">
                  <thead>
                    <tr>
                      <th>输入</th>
                      <th>规范化域名</th>
                      <th>长度</th>
                      <th>后缀</th>
                      <th>类型</th>
                      <th>分类</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(preview.rows ?? []).slice(0, 300).map((r, i) => (
                      <tr key={i} className={r.class === 'NEW' ? '' : 'dim'}>
                        <td>{r.raw}</td>
                        <td>{r.domain || '-'}</td>
                        <td>{r.charCount || '-'}</td>
                        <td>{r.suffix || '-'}</td>
                        <td>{r.style || '-'}</td>
                        <td>
                          <Badge tone={CLASS_TONE[r.class]}>{CLASS_LABEL[r.class] ?? r.class}</Badge>
                          {r.reason && <code className="reason">{r.reason}</code>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="row end">
                <button type="button" className="primary" disabled={!commitable} onClick={() => void doCommit()}>
                  {op.busy ? '提交中…' : `提交入库 ${preview.counts.new} 个`}
                </button>
              </div>
              <Notice
                error={op.error}
                extra={
                  op.error ? (
                    <span className="sub">
                      修正后再次点击会使用同一请求编号：若首次提交其实已落库，服务器会返回原结果而不会重复写入。
                    </span>
                  ) : undefined
                }
              />
            </>
          )}
          {op.result && !preview && (
            <div className="notice ok" role="status">
              <strong>
                入库成功：新写入 {op.result.inserted} 个域名
                {op.result.skipped > 0 && `，跳过 ${op.result.skipped} 个已在库的`}
              </strong>
              <div className="sub">
                批次 <Link to={`/batches?highlight=${op.result.batchId}`}>{op.result.batchCode}</Link> · 请求编号{' '}
                {op.result.requestId}
              </div>
              {(op.result.skippedDomains ?? []).length > 0 && (
                <details className="sub">
                  <summary>跳过的域名（{op.result.skipped}）</summary>
                  <div className="mono wrap-list">{op.result.skippedDomains?.join(' · ')}</div>
                </details>
              )}
            </div>
          )}
          {plat.loading && <p className="muted">载入平台名单…</p>}
        </div>
      </div>
    </section>
  )
}
